import React from "react";

/**
 * Reusable autoplay section video with subtle 20s zoom-in/out animation.
 * - muted + playsInline + loop + autoPlay for inline hero-style playback
 * - preload=metadata to keep bandwidth low
 * - scale 1.0 → 1.08 over 20s, infinite alternate — makes static clips feel alive
 */
export default function SectionVideo({ src, poster, title, subtitle, badge }) {
  return (
    <section className="site-container py-7 md:py-10">
      <div className="relative surface-panel overflow-hidden aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]">
        <video
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover opacity-90 section-video-anim"
          data-testid="section-video"
        />
        {/* subtle vignette only — video stays clearly visible */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(90deg, rgba(10,31,56,0.78) 0%, rgba(10,31,56,0.45) 35%, rgba(10,31,56,0.0) 65%)"
        }}/>
        <div className="absolute inset-x-0 bottom-0 pointer-events-none h-2/5" style={{
          background: "linear-gradient(180deg, rgba(10,31,56,0.0) 0%, rgba(10,31,56,0.75) 100%)"
        }}/>
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 lg:p-12">
          {badge && (
            <div className="eyebrow mb-3 text-[#5FA637]" data-testid="section-video-badge">{badge}</div>
          )}
          {title && (
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl drop-shadow-lg">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mt-3 text-sm md:text-base text-white/90 max-w-2xl leading-relaxed drop-shadow">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes section-video-zoom {
          0%   { transform: scale(1.0) translate(0, 0); }
          50%  { transform: scale(1.08) translate(-1%, -0.5%); }
          100% { transform: scale(1.0) translate(0, 0); }
        }
        .section-video-anim {
          animation: section-video-zoom 20s ease-in-out infinite;
          transform-origin: center center;
          will-change: transform;
        }
        @media (prefers-reduced-motion: reduce) {
          .section-video-anim { animation: none; }
        }
      `}</style>
    </section>
  );
}
